import { useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Badge } from '@/components/ui/badge';

export interface LogEntry {
  eventId: string;
  eventTime: string;
  level: string;
  message: string;
  service?: string;
  environment?: string;
  traceId?: string;
  attributes?: Record<string, unknown>;
}

interface LogsTableProps {
  logs: LogEntry[];
  height?: number;
}

const getLevelVariant = (level: string): 'destructive' | 'warning' | 'default' | 'secondary' => {
  switch (level) {
    case 'error':
    case 'fatal':
      return 'destructive';
    case 'warn':
      return 'warning';
    case 'info':
      return 'default';
    case 'debug':
    default:
      return 'secondary';
  }
};

const formatTime = (timestamp: string): string => {
  return new Date(timestamp).toLocaleTimeString();
};

export const LogsTable = ({ logs, height = 500 }: LogsTableProps) => {
  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: logs.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 48,
    overscan: 5,
  });

  if (logs.length === 0) {
    return (
      <div
        data-testid="logs-table-container"
        className="overflow-auto border border-border"
        style={{ height: `${height}px` }}
      >
        <table role="table" className="w-full border-collapse">
          <thead>
            <tr className="border-b border-border bg-muted">
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">
                Time
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">
                Level
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">
                Service
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">
                Message
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan={4} className="py-10 text-center text-muted-foreground">
                No logs to display
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div
      ref={parentRef}
      data-testid="logs-table-container"
      className="overflow-auto border border-border"
      style={{ height: `${height}px` }}
    >
      <table role="table" className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
        <thead>
          <tr className="sticky top-0 z-10 border-b border-border bg-muted">
            <th
              className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground"
              style={{ width: '140px' }}
            >
              Time
            </th>
            <th
              className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground"
              style={{ width: '80px' }}
            >
              Level
            </th>
            <th
              className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground"
              style={{ width: '120px' }}
            >
              Service
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">
              Message
            </th>
          </tr>
        </thead>
        <tbody
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            position: 'relative',
            display: 'block',
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const log = logs[virtualRow.index];
            return (
              <tr
                key={log.eventId}
                className="border-b border-border/50 hover:bg-muted/50"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                  display: 'table',
                  tableLayout: 'fixed',
                }}
              >
                <td className="px-4 py-3 align-top" style={{ width: '140px' }}>
                  <span
                    data-testid="log-time"
                    className="font-mono text-sm text-muted-foreground"
                  >
                    {formatTime(log.eventTime)}
                  </span>
                </td>
                <td className="px-4 py-3 align-top" style={{ width: '80px' }}>
                  <Badge variant={getLevelVariant(log.level)} className="text-xs">
                    {log.level.toUpperCase()}
                  </Badge>
                </td>
                <td className="px-4 py-3 align-top" style={{ width: '120px' }}>
                  {log.service && (
                    <span className="font-mono text-sm text-purple-400">{log.service}</span>
                  )}
                </td>
                <td className="px-4 py-3 align-top">
                  <div className="font-mono text-sm text-foreground">{log.message}</div>
                  {log.traceId && (
                    <div className="mt-1 text-xs text-muted-foreground">trace: {log.traceId}</div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
